import { FC, useEffect, useState } from 'react';
import { useFormik, FormikProvider } from 'formik';
import * as yup from 'yup';
import {
  Typography,
  Row,
  Col,
  Input,
  Layout,
  Icons,
  Form,
  Alert,
  Spin,
  message,
  Switch,
  Button,
  Space,
} from 'taxaroo-ui';

import SubmitButton from '~src/components/Formik/SubmitButton';
import ActionButton from '~src/components/molecules/ActionButton';
import { ApolloError } from '@apollo/client';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  GetExternalCalendarsDocument,
  GetFirmSettingDocument,
  GetNylasAccountsDocument,
  useGetExternalCalendarsQuery,
  useGetFirmSettingQuery,
  useGetNylasAccountsQuery,
} from '~src/graphql/queries/settings';
import {
  useCreateFirmSettingMutation,
  useDeleteNylasAccountMutation,
  useDisconnectExternalCalendarMutation,
  useGetNylasAuthUrlMutation,
  useHandleNylasAuthCallbackMutation,
  useUpdateFirmSettingMutation,
} from '~src/graphql/mutations/settings';
import { useGetCalendarOauthUrlLazyQuery } from '~src/graphql/queries/appointments';
import SettingsHeader from '../SettingsHeader';

const {
  DeleteOutlined, ToolFilled, WarningFilled, CheckCircleFilled,
} = Icons;
const { Paragraph, Text, Title } = Typography;
const validationSchema = yup.object().shape({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
});

const Calendars: FC = () => {
  const navigate = useNavigate();
  // const [getAuthUrl, { loading: loadingAuthUrl, error: errorAuthUrl }] =
  //   useGetNylasAuthUrlMutation();
  const [getCalendarOauthUrl, { loading: loadingCalendarOauthUrl, error: errorCalendarOauthUrl }] = useGetCalendarOauthUrlLazyQuery();
  // const handleEmailSync = async (email: string) => {
  //   const result = await getAuthUrl({
  //     variables: {
  //       input: {
  //         email,
  //         callbackUrl: `${window.location.origin}/settings/calendars`,
  //       },
  //     },
  //   });

  //   window.location.href = result.data.getNylasAuthUrl.url;
  // };

  // const formik = useFormik({
  //   initialValues: { email: '' },
  //   validationSchema,
  //   onSubmit: (values) => {
  //     handleEmailSync(values.email);
  //   },
  // });
  // const { touched, values, errors, handleSubmit, handleChange } = formik;
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnError = (error: ApolloError) => {
    message.error(`An error has occurred: ${error.message}.`);
  };

  // const {
  //   data: accounts,
  //   loading: loadingAccounts,
  //   error: errorAccounts,
  // } = useGetNylasAccountsQuery();

  const {
    data: externalCalendars,
    loading: loadingExternalCalendars,
    error: errorExternalCalendars,
  } = useGetExternalCalendarsQuery();

  // const [deleteNylasAccountMutation, { loading: loadingDeleteNylasAccount }] =
  //   useDeleteNylasAccountMutation({
  //     onError: handleOnError,
  //     refetchQueries: [GetNylasAccountsDocument],
  //     awaitRefetchQueries: true,
  //   });
  const [disconnectExternalCalendar, { loading: loadingDisconnectExternalCalendar }] = useDisconnectExternalCalendarMutation({
    onError: handleOnError,
    refetchQueries: [GetExternalCalendarsDocument],
    awaitRefetchQueries: true,
  });

  const handleDisconnectCalendar = async (externalCalendarId: string) => {
    await disconnectExternalCalendar({ variables: { externalCalendarId } });
  };

  const [authUrlCallback, { loading: loadingAuthUrlCallback }] = useHandleNylasAuthCallbackMutation(
    {
      onError: handleOnError,
      refetchQueries: [GetNylasAccountsDocument],
      awaitRefetchQueries: true,
    },
  );

  const {
    data: firmSettingsData,
    loading: firmSettingsLoading,
    error: firmSettingsError,
  } = useGetFirmSettingQuery({ variables: { name: 'disable-taxaroo-calendar' } });
  const [disableTaxarooCalendarValue, setDisableTaxarooCalendarValue] = useState<boolean>(false);

  const [createFirmSetting, { loading: createFirmSettingLoading }] = useCreateFirmSettingMutation({
    onError: handleOnError,
    refetchQueries: [
      { query: GetFirmSettingDocument, variables: { name: 'disable-taxaroo-calendar' } },
    ],
    awaitRefetchQueries: true,
  });
  const [updateFirmSetting, { loading: updateFirmSettingLoading }] = useUpdateFirmSettingMutation({
    onError: handleOnError,
    refetchQueries: [
      { query: GetFirmSettingDocument, variables: { name: 'disable-taxaroo-calendar' } },
    ],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (firmSettingsData?.getFirmSetting?.name) {
      // eslint-disable-next-line no-underscore-dangle
      if (firmSettingsData.getFirmSetting.value.__typename === 'BooleanValue') {
        setDisableTaxarooCalendarValue(firmSettingsData.getFirmSetting.value.boolValue);
      }
    }
  }, [firmSettingsData?.getFirmSetting]);

  useEffect(() => {
    if (searchParams.get('state') && searchParams.get('code')) {
      const code = searchParams.get('code');
      const handleAuthUrlCallback = async (theCode: string) => {
        await authUrlCallback({ variables: { input: { code: theCode } } });
      };
      handleAuthUrlCallback(code);
    }
  }, []);

  useEffect(() => {
    if (errorExternalCalendars) {
      return handleOnError(errorExternalCalendars);
    }
    if (errorCalendarOauthUrl) {
      return handleOnError(errorCalendarOauthUrl);
    }
    if (firmSettingsError) {
      return handleOnError(firmSettingsError);
    }
    return null;
  }, [errorExternalCalendars, errorCalendarOauthUrl, firmSettingsError]);

  const changeDisableTaxarooCalendarSetting = async (val: boolean) => {
    if (firmSettingsData?.getFirmSetting?.id) {
      await updateFirmSetting({
        variables: {
          id: firmSettingsData.getFirmSetting.id,
          value: String(val),
        },
      });
    } else {
      await createFirmSetting({
        variables: {
          name: firmSettingsData.getFirmSetting.name,
          value: String(val),
        },
      });
    }
  };

  const handleConnectExternalCalendar = (provider: string) => {
    getCalendarOauthUrl({
      variables: {
        provider,
      },
      onCompleted: (data) => {
        window.location.href = data.getCalendarOauthUrl;
      },
    });
  };

  return (
    <Layout style={{ backgroundColor: 'white' }}>
      <SettingsHeader
        title="Calendars"
        tooltipTitle="What do we do with this information?"
        tooltipContent={(
          <Paragraph>
            <Text>Calendars</Text>
          </Paragraph>
        )}
      />
      <Col style={{ paddingTop: '10px' }}>
        <Typography>
          <Paragraph>
            <Text>Synchronize your Google or Outlook calendar</Text>
          </Paragraph>
        </Typography>
        <Row gutter={15} style={{ marginBottom: '15px' }}>
          <Col>
            <Button onClick={() => handleConnectExternalCalendar('GOOGLE')}>
              Google Calendar
            </Button>
          </Col>
          <Col>
            <Button onClick={() => handleConnectExternalCalendar('MICROSOFT')}>
              Microsoft Outlook
            </Button>
          </Col>
        </Row>

        {/* <FormikProvider value={formik}>
          <Form
            name="basic"
            onFinish={handleSubmit}
            style={{ paddingBottom: '25px' }}
          >
            <Row align="middle">
              <Input
                style={{ width: '50%' }}
                name="email"
                onChange={handleChange}
                value={values.email}
                placeholder="Email"
                disabled={loadingAccounts || loadingDeleteNylasAccount || loadingDeleteNylasAccount}
              />
              {loadingAuthUrl
                ? <Spin size="large" />
                : (
                  <SubmitButton
                    label="+"
                    disabled={loadingAccounts || loadingDeleteNylasAccount || loadingDeleteNylasAccount}
                  />
                )}
            </Row>
            <Row align="middle">
              {touched.email && errors.email && (
              <Alert
                message={errors.email}
                type="error"
                style={{ width: '40%' }}
              />
              )}
            </Row>
          </Form>
        </FormikProvider> */}

        {loadingExternalCalendars || loadingDisconnectExternalCalendar || loadingAuthUrlCallback ? (
          <Row align="middle" justify="center">
            <Spin size="large" />
          </Row>
        ) : (
          externalCalendars?.getExternalCalendars.map((account) => (
            <Row key={account.id} style={{ paddingBottom: '10px' }}>
              <Col style={{ width: '50%' }}>
                <Space size="middle">
                  <Text>{account.provider}</Text>
                  <Text>{account.connectedEmail}</Text>
                </Space>
              </Col>
              <Col>
                <Row gutter={15}>
                  {/* {account.syncState.includes('invalid')
                    && (
                    <Col>
                      {loadingAuthUrl
                        ? <Spin size="large" />
                        : (
                          <ActionButton
                            tooltip="Please try to authenticating again or use a different email account"
                            confirmAction={false}
                            onClick={() => handleEmailSync(account.emailAddress)}
                            buttonProps={{ type: 'default' }}
                          >
                            <ToolFilled />
                          </ActionButton>
                        )}
                    </Col>
                    )} */}
                  <Col>
                    <ActionButton
                      tooltip="Disconnect Calendar"
                      confirmAction
                      confirmLabel="Are you sure?"
                      onClick={() => handleDisconnectCalendar(account.id)}
                      buttonProps={{ type: 'primary' }}
                    >
                      <DeleteOutlined />
                    </ActionButton>
                  </Col>
                </Row>
              </Col>
            </Row>
          ))
        )}
      </Col>

      <Row style={{ paddingTop: '20px' }}>
        <Row style={{ width: '100%' }}>
          <Title level={5}>Settings</Title>
        </Row>

        {firmSettingsLoading && (
          <Row align="middle" justify="center">
            <Spin size="large" />
          </Row>
        )}

        {firmSettingsData?.getFirmSetting?.description && (
          <Row gutter={15} align="middle">
            <Col style={{ width: '60%' }}>
              <Text>{firmSettingsData.getFirmSetting.description}</Text>
            </Col>
            <Col>
              <Switch
                onChange={(val: boolean) => changeDisableTaxarooCalendarSetting(val)}
                checked={disableTaxarooCalendarValue}
                loading={createFirmSettingLoading || updateFirmSettingLoading}
              />
            </Col>
          </Row>
        )}
      </Row>
    </Layout>
  );
};

export default Calendars;
